import React, { useState, useRef, useEffect } from 'react';
import { questionAPI } from '../../services/api';
import { Send, Loader2, User, Bot } from 'lucide-react';

function Message({ msg }) {
  const isUser = msg.from === 'user';
  
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isUser ? 'bg-black' : 'bg-gray-200'
      }`}>
        {isUser ? (
          <User size={16} className="text-white" />
        ) : (
          <Bot size={16} className="text-gray-700" />
        )}
      </div>
      
      <div className={`max-w-[75%] px-4 py-3 rounded-2xl ${
        isUser 
          ? 'bg-black text-white rounded-tr-sm' 
          : 'bg-gray-100 text-gray-900 rounded-tl-sm'
      }`}>
        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
          {msg.text}
        </p>
      </div>
    </div>
  );
}

export default function ChatBox({ projectId }) {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef();
  const chatContainerRef = useRef();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs]);

  const send = async e => {
    e.preventDefault();
    if (!q.trim() || sending) return;

    const userMsg = { from: 'user', text: q };
    setMsgs(m => [...m, userMsg]);
    setQ('');
    setSending(true);
    
    try {
      const res = await questionAPI.ask(projectId, q);
      setMsgs(m => [...m, { 
        from: 'bot', 
        text: res.data.answer || res.answer 
      }]);
    } catch (err) {
      setMsgs(m => [...m, { 
        from: 'bot', 
        text: 'Sorry, I encountered an error processing your question. Please try again.' 
      }]);
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col h-[600px]">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900">Ask Questions</h3>
        <p className="text-sm text-gray-600 mt-1">
          Chat with your codebase using natural language
        </p>
      </div>
      
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50"
      >
        {msgs.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-4">
                <Bot size={32} className="text-gray-500" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Start a conversation
              </h4>
              <p className="text-sm text-gray-600">
                Ask questions about your codebase, request explanations, or explore functionality
              </p>
            </div>
          </div>
        ) : (
          <>
            {msgs.map((m, i) => <Message key={i} msg={m} />)}
            {sending && (
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gray-200">
                  <Bot size={16} className="text-gray-700" />
                </div>
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-2xl rounded-tl-sm">
                  <Loader2 size={16} className="animate-spin text-gray-600" />
                  <span className="text-sm text-gray-600">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </>
        )}
      </div>
      
      <div className="border-t border-gray-200 p-4 bg-white">
        <form onSubmit={send} className="flex gap-3">
          <input
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all text-sm"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Ask about your codebase..."
            disabled={sending}
          />
          <button 
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
            disabled={sending || !q.trim()}
          >
            {sending ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </form>
      </div>
    </div>
  );
}